import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users, Search, Plus, Pencil, Trash2, Download,
  Loader2, X, User, Mail, Building2, AlertCircle, ArrowLeft
} from "lucide-react";
import { supabase } from "../../lib/supabaseClient";
import { useToast } from "../ui/Toast";
import ConfirmDialog from "../ui/ConfirmDialog";
import * as XLSX from "xlsx";

const AdminStudents = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [students, setStudents] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [collegeFilter, setCollegeFilter] = useState("all");

  const [showModal, setShowModal] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  const [formName, setFormName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formCollege, setFormCollege] = useState("");
  const [formErrors, setFormErrors] = useState({});

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("volunteers")
      .select("*")
      .order("created_at", { ascending: false });

    if (!error && data) setStudents(data);
    setLoading(false);
  }, []);

  const fetchColleges = useCallback(async () => {
    const { data } = await supabase.from("colleges").select("id, name").order("name");
    if (data) setColleges(data);
  }, []);

  useEffect(() => {
    fetchStudents();
    fetchColleges();
  }, [fetchStudents, fetchColleges]);

  const filteredStudents = students.filter((s) => {
    if (collegeFilter !== "all" && s.college_name !== collegeFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!s.name?.toLowerCase().includes(q) && !s.email?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const openAddModal = () => {
    setEditingStudent(null);
    setFormName("");
    setFormEmail("");
    setFormCollege("");
    setFormErrors({});
    setShowModal(true);
  };

  const openEditModal = (student) => {
    setEditingStudent(student);
    setFormName(student.name);
    setFormEmail(student.email);
    setFormCollege(student.college_name);
    setFormErrors({});
    setShowModal(true);
  };

  const validateForm = () => {
    const errs = {};
    if (!formName.trim()) errs.name = "Name is required";
    if (!formEmail.trim()) errs.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formEmail.trim())) errs.email = "Invalid email";
    if (!formCollege) errs.college = "College is required";
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setModalLoading(true);

    if (editingStudent) {
      const { error } = await supabase
        .from("volunteers")
        .update({
          name: formName.trim(),
          email: formEmail.trim().toLowerCase(),
          college_name: formCollege,
        })
        .eq("id", editingStudent.id);

      if (error) {
        toast.error("Failed to update: " + error.message);
      } else {
        toast.success("Student updated successfully");
        setShowModal(false);
        fetchStudents();
      }
    } else {
      const { error } = await supabase.from("volunteers").insert({
        name: formName.trim(),
        email: formEmail.trim().toLowerCase(),
        college_name: formCollege,
      });

      if (error) {
        toast.error("Failed to add: " + error.message);
      } else {
        toast.success("Student added successfully");
        setShowModal(false);
        fetchStudents();
      }
    }
    setModalLoading(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);

    const { error } = await supabase.from("volunteers").delete().eq("id", deleteTarget.id);
    if (error) {
      toast.error("Failed to delete: " + error.message);
    } else {
      toast.success("Student deleted");
      fetchStudents();
    }
    setDeleteTarget(null);
    setDeleteLoading(false);
  };

  const handleExport = () => {
    const exportData = filteredStudents.map((s) => ({
      Name: s.name,
      Email: s.email,
      College: s.college_name,
      "Created At": new Date(s.created_at).toLocaleDateString("en-IN"),
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Students");
    XLSX.writeFile(wb, `KU_Students_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success("Excel file downloaded");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      month: "short", day: "2-digit", year: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-ku-cream">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#1a1a2e] via-[#2d1b4e] to-[#3B1E60] text-white py-8 md:py-10 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <button
                type="button"
                onClick={() => navigate("/admin/dashboard")}
                className="inline-flex items-center gap-1.5 text-xs font-bold text-ku-gold hover:text-amber-300 transition-colors bg-transparent border-none cursor-pointer mb-3 p-0"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Dashboard
              </button>
              <h1 className="text-2xl sm:text-3xl font-black tracking-tight">Student Management</h1>
              <p className="text-white/60 text-sm mt-1 font-medium">{students.length} volunteers registered</p>
            </div>
            <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
              <button
                type="button"
                onClick={handleExport}
                disabled={filteredStudents.length === 0}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 border border-white/20 text-white text-sm font-bold hover:bg-white/20 transition-colors cursor-pointer disabled:opacity-40"
              >
                <Download className="w-4 h-4" /> Export Excel
              </button>
              <button
                type="button"
                onClick={openAddModal}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-ku-gold text-ku-purple-deep text-sm font-bold hover:bg-amber-400 transition-colors cursor-pointer shadow-sm border-none"
              >
                <Plus className="w-4 h-4" /> Add Student
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="bg-white rounded-2xl border border-ku-soft/50 shadow-sm p-4 mb-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-ku-purple bg-ku-cream/30 focus:border-ku-purple outline-none"
            />
          </div>
          <select
            value={collegeFilter}
            onChange={(e) => setCollegeFilter(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-ku-purple bg-ku-cream/30 focus:border-ku-purple outline-none"
          >
            <option value="all">All Colleges</option>
            {colleges.map((c) => (
              <option key={c.id} value={c.name}>{c.name}</option>
            ))}
          </select>
        </div>

        <div className="bg-white rounded-2xl border border-purple-100 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-ku-purple animate-spin" />
            </div>
          ) : filteredStudents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <Users className="w-12 h-12 mb-3 text-purple-300" />
              <p className="font-bold text-gray-500">No students found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[600px]">
                <thead className="bg-[#FAF7FD] border-b border-purple-100">
                  <tr className="text-[11px] uppercase tracking-wider font-extrabold text-purple-900">
                    <th className="py-4 px-5">Name</th>
                    <th className="py-4 px-4">Email</th>
                    <th className="py-4 px-4">College</th>
                    <th className="py-4 px-4">Joined</th>
                    <th className="py-4 px-5 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-sm">
                  {filteredStudents.map((student) => (
                    <tr key={student.id} className="hover:bg-purple-50/30">
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-ku-purple to-ku-violet flex items-center justify-center text-white text-xs font-black">
                            {student.name?.charAt(0)?.toUpperCase() || "?"}
                          </div>
                          <span className="font-bold text-gray-900">{student.name}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-gray-600 text-xs font-medium">{student.email}</td>
                      <td className="py-4 px-4">
                        <span className="text-xs font-bold text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full">
                          {student.college_name}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-xs text-gray-500 font-medium">{formatDate(student.created_at)}</td>
                      <td className="py-4 px-5">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            type="button"
                            onClick={() => openEditModal(student)}
                            className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 border-none cursor-pointer"
                            title="Edit"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteTarget(student)}
                            className="p-2 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 border-none cursor-pointer"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add / Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowModal(false)} className="fixed inset-0 z-[9990] bg-black/40 backdrop-blur-sm" />
            <motion.div initial={{ opacity: 0, scale: 0.92, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.92, y: 20 }} className="fixed inset-0 z-[9991] flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-elevated border border-ku-soft/60 w-full max-w-lg overflow-hidden">
                <div className="flex items-center justify-between p-6 pb-4 border-b border-ku-soft/50">
                  <h3 className="text-lg font-black text-ku-purple tracking-tight">{editingStudent ? "Edit Student" : "Add Student"}</h3>
                  <button type="button" onClick={() => setShowModal(false)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 bg-transparent border-none cursor-pointer"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-6 space-y-4">
                  <div className="space-y-1.5">
                    <label className="block text-xs font-black text-ku-purple uppercase tracking-wider opacity-80">Name *</label>
                    <div className="relative">
                      <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      <input type="text" value={formName} onChange={(e) => { setFormName(e.target.value); setFormErrors((p) => ({ ...p, name: "" })); }} placeholder="Full name" className="w-full pl-11 pr-4 py-3 rounded-xl border text-sm font-bold text-ku-purple outline-none bg-ku-cream/30 focus:bg-white" />
                    </div>
                    {formErrors.name && <p className="text-xs font-bold text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{formErrors.name}</p>}
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-xs font-black text-ku-purple uppercase tracking-wider opacity-80">Email *</label>
                    <div className="relative">
                      <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      <input type="email" value={formEmail} onChange={(e) => { setFormEmail(e.target.value); setFormErrors((p) => ({ ...p, email: "" })); }} placeholder="email@example.com" disabled={!!editingStudent} className="w-full pl-11 pr-4 py-3 rounded-xl border text-sm font-bold text-ku-purple outline-none bg-ku-cream/30 focus:bg-white disabled:opacity-60" />
                    </div>
                    {formErrors.email && <p className="text-xs font-bold text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{formErrors.email}</p>}
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-xs font-black text-ku-purple uppercase tracking-wider opacity-80">College *</label>
                    <div className="relative">
                      <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      <select value={formCollege} onChange={(e) => { setFormCollege(e.target.value); setFormErrors((p) => ({ ...p, college: "" })); }} className="w-full pl-11 pr-4 py-3 rounded-xl border text-sm font-bold text-ku-purple outline-none bg-ku-cream/30 focus:bg-white">
                        <option value="">Select college</option>
                        {colleges.map((c) => (
                          <option key={c.id} value={c.name}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                    {formErrors.college && <p className="text-xs font-bold text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{formErrors.college}</p>}
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 p-6 pt-0">
                  <button type="button" onClick={() => setShowModal(false)} className="px-5 py-2.5 rounded-xl text-sm font-bold text-gray-600 bg-gray-100 border-none cursor-pointer">Cancel</button>
                  <button type="button" onClick={handleSave} disabled={modalLoading} className="px-5 py-2.5 rounded-xl text-sm font-bold text-white bg-ku-purple hover:bg-ku-violet border-none cursor-pointer shadow-sm disabled:opacity-50 flex items-center gap-2">
                    {modalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : editingStudent ? "Update Student" : "Add Student"}
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Student"
        message={`Are you sure you want to delete "${deleteTarget?.name}"?`}
        confirmText="Delete Student"
        loading={deleteLoading}
      />
    </div>
  );
};

export default AdminStudents;